import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { ZSNCampaign, ZSNCampaignConfiguration, ZSNOrder, ZSNOrderItem, ZSNParcel } from '../models/zsn.model';
import { Parcel } from '../models/parcel.model';
import { Page } from '../models/page.model';
// FIXME sortir de AR => commun à tous les outils
import { AROrderFilter, ARResultService, OrderSummary, fillOrderFilter } from './agrorendement-common.service';
import { HttpWanaka } from '../utils/http.wanaka';

export class ZSNParcelFilter {
  prefixParcel: string;
  prefixExploitation: string;
  farmId: number | string;
  campaignId: number | number[];
  organisationId: number | string;
  orderStatus: boolean;
  completionStatus: boolean;
  prefixIdParcel: number;
}

@Injectable({ providedIn: 'root' })
export class ZSNService extends ARResultService<ZSNOrderItem, ZSNCampaign> {
  protected toolUrl = '/tools/zsn';

  constructor(
    protected http: HttpClient,
    protected httpWanaka: HttpWanaka,
  ) {
    super(http, httpWanaka);
  }

  protected handleError(error: any) {
    // FIXME
    console.error('An error occurred', error);
    return throwError(() => error.message || error);
  }

  getPageCampaigns(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: string,
  ): Observable<Page<ZSNCampaign>> {
    let url = this.toolUrl + '/campaigns?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    // filter pas supporté pour l'instant
    if (filter) {
      url += '&year=' + filter;
    }
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        data.content.forEach((element: ZSNCampaign) => {
          this.handleCampaignDates(element);
          if (element.configuration) {
            this.handleConfigurationDates(element.configuration);
          }
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaigns(): Observable<ZSNCampaign[]> {
    return this.http.get<any>(this.toolUrl + '/campaigns/all?configurationStatus=true').pipe(
      map((response) => {
        const data: ZSNCampaign[] = response.data;
        data.forEach((element) => {
          this.handleCampaignDates(element);
          if (element.configuration) {
            this.handleConfigurationDates(element.configuration);
          }
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaign(id: number): Observable<ZSNCampaign> {
    return this.http.get<any>(this.toolUrl + '/campaigns/' + id).pipe(
      map((response) => {
        const data: ZSNCampaign = response.data;
        this.handleCampaignDates(data);
        if (data.configuration) {
          this.handleConfigurationDates(data.configuration);
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaignConfigurations(): Observable<ZSNCampaignConfiguration[]> {
    return this.http.get<any>(this.toolUrl + '/configs/all').pipe(
      map((response) => {
        const data: ZSNCampaignConfiguration[] = response.data;
        data.forEach((element: ZSNCampaignConfiguration) => {
          this.handleConfigurationDates(element);
          if (element.campaign) {
            this.handleCampaignDates(element.campaign);
          }
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaignConfiguration(id: number): Observable<ZSNCampaignConfiguration> {
    return this.http.get<any>(this.toolUrl + '/configs/' + id).pipe(
      map((response) => {
        const data: ZSNCampaignConfiguration = this.handleConfigurationDates(response.data);
        if (data.campaign) {
          this.handleCampaignDates(data.campaign);
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  saveCampaignConfiguration(config: ZSNCampaignConfiguration): Observable<ZSNCampaignConfiguration> {
    if (config.id) {
      return this.putCampaignConfiguration(config);
    }
    return this.postCampaignConfiguration(config);
  }

  deleteCampaignConfiguration(config: ZSNCampaignConfiguration) {
    const url = `${this.toolUrl}/configs/${config.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  // Add new campaign
  private postCampaignConfiguration(config: ZSNCampaignConfiguration): Observable<ZSNCampaignConfiguration> {
    return this.http.post<any>(this.toolUrl + '/configs', config).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private putCampaignConfiguration(config: ZSNCampaignConfiguration) {
    const url = `${this.toolUrl}/configs/${config.id}`;

    return this.http.put<any>(url, config).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private handleCampaignDates(campaign: ZSNCampaign): ZSNCampaign {
    campaign.startingOn = new Date(<string>campaign.startingOn);
    campaign.endingOn = new Date(<string>campaign.endingOn);
    campaign.createdAt = new Date(<string>campaign.createdAt);
    campaign.updatedAt = new Date(<string>campaign.updatedAt);

    return campaign;
  }

  private handleConfigurationDates(config: ZSNCampaignConfiguration): ZSNCampaignConfiguration {
    if (config.startingOn) {
      config.startingOn = new Date(<string>config.startingOn);
    }
    if (config.endingOn) {
      config.endingOn = new Date(<string>config.endingOn);
    }

    config.createdAt = new Date(<string>config.createdAt);
    config.updatedAt = new Date(<string>config.updatedAt);

    return config;
  }

  private handleOrderDates(order: ZSNOrder): ZSNOrder {
    order.createdAt = new Date(<string>order.createdAt);
    order.updatedAt = new Date(<string>order.updatedAt);

    return order;
  }

  getPageParcels(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: ZSNParcelFilter,
  ): Observable<Page<ZSNParcel>> {
    let url = this.toolUrl + '/parcels?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    // filter pas supporté pour l'instant
    url += this.buildParamsFromFilter(filter);
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        data.content.forEach((element: ZSNParcel) => {
          this.handleParcel(element);
          if (element.campaign) {
            this.handleCampaignDates(<ZSNCampaign>element.campaign);
          }
          if ((<ZSNCampaign>element.campaign).configuration) {
            this.handleConfigurationDates((<ZSNCampaign>element.campaign).configuration);
          }
        });

        return data;
      }),
      catchError(this.handleError),
    );
  }

  getAllParcelIds(filter: ZSNParcelFilter): Observable<number[]> {
    let url = this.toolUrl + '/parcels/all?onlyIds=true';
    url += this.buildParamsFromFilter(filter);
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        return data.map((x: ZSNParcel) => x.id);
      }),
      catchError(this.handleError),
    );
  }

  buildParamsFromFilter(filter: ZSNParcelFilter): string {
    let url = '';
    if (filter) {
      if (filter.prefixParcel) {
        url += '&prefixParcel=' + filter.prefixParcel;
      }
      if (filter.prefixExploitation) {
        url += '&prefixExploitation=' + filter.prefixExploitation;
      }
      if (filter.prefixIdParcel) {
        url += '&prefixIdParcel=' + filter.prefixIdParcel;
      }
      if (filter.farmId) {
        url += '&farm=' + filter.farmId;
      }
      if (filter.organisationId) {
        url += '&organisation=' + filter.organisationId;
      }
      if (filter.campaignId) {
        if (typeof filter.campaignId === 'number' || typeof filter.campaignId === 'string') {
          url += '&campaign=' + filter.campaignId;
        } else {
          url += '&campaign=' + (<number[]>filter.campaignId).join(',');
        }
      }
      if (filter.orderStatus === true || filter.orderStatus === false) {
        url += '&orderStatus=' + filter.orderStatus;
      }
      if (filter.completionStatus === true || filter.completionStatus === false) {
        url += '&completionStatus=' + filter.completionStatus;
      }
    }
    return url;
  }

  handleParcel(parcel: ZSNParcel): ZSNParcel {
    parcel.createdAt = new Date(<string>parcel.createdAt);
    parcel.updatedAt = new Date(<string>parcel.updatedAt);
    return parcel;
  }

  getParcel(id: number): Observable<ZSNParcel> {
    return this.http.get<any>(this.toolUrl + '/parcels/' + id).pipe(
      map((response) => {
        const data: ZSNParcel = response.data;
        this.handleParcel(data);
        if (data.campaign) {
          this.handleCampaignDates(<ZSNCampaign>data.campaign);
        }
        if ((<ZSNCampaign>data.campaign).configuration) {
          this.handleConfigurationDates((<ZSNCampaign>data.campaign).configuration);
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getOrderItems(
    orderItemIds: Array<number>,
    completionStatus: boolean,
    orderStatus: boolean,
  ): Observable<ZSNOrderItem[]> {
    let url = this.toolUrl + '/orderitems/all';
    url += this.fillOrderItemFilter(orderItemIds, completionStatus, orderStatus);

    return this.http.get<any>(url).pipe(
      map((response) => {
        const data: ZSNOrderItem[] = response.data;
        return data;
      }),
      catchError(this.handleError),
    );
  }

  countOrderItems(
    completionStatus: boolean,
    orderStatus: boolean,
    orderableStatus: boolean = null,
  ): Observable<number> {
    let url = this.toolUrl + '/orderitems/count';
    url += this.fillOrderItemFilter(null, completionStatus, orderStatus);

    return this.http.get<any>(url).pipe(
      map((response) => {
        const data: number = response.data;
        return data;
      }),
      catchError(this.handleError),
    );
  }

  // FIXME commun à tous les services agrorendement
  fillOrderItemFilter(orderItemIds: Array<number>, completionStatus: boolean, orderStatus: boolean) {
    let url = '';
    // FIXME traiter orderItemIds
    if (orderItemIds !== null || completionStatus !== null || orderStatus !== null) {
      let q = '';
      if (completionStatus !== null) {
        q += 'completionStatus=' + completionStatus;
      }
      if (orderStatus !== null) {
        if (q.length > 0) {
          q += '&';
        }
        q += 'orderStatus=' + orderStatus;
      }
      if (orderItemIds !== null) {
        if (q.length > 0) {
          q += '&';
        }
        q += 'orderItemIds=' + orderItemIds.join(',');
      }
      url += '?' + q;
    }

    return url;
  }

  getOrderItem(id: number): Observable<ZSNOrderItem> {
    return this.http.get<any>(this.toolUrl + '/orderitems/' + id).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  getOrderItemByParcel(parcel: Parcel): Observable<ZSNOrderItem> {
    return this.http.get<any>(this.toolUrl + '/orderitems/parcel/' + parcel.id).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  saveOrderItem(orderItem: ZSNOrderItem): Observable<ZSNOrderItem> {
    if (orderItem.id) {
      return this.putOrderItem(orderItem);
    }
    return this.postOrderItem(orderItem);
  }

  deleteOrderItem(orderItem: ZSNOrderItem) {
    const url = `${this.toolUrl}/orderitems/${orderItem.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  deleteParcelOrderItem(parcel: Parcel) {
    const url = `${this.toolUrl}/orderitems/parcel/${parcel.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  // Add new orderItem
  private postOrderItem(config: ZSNOrderItem): Observable<ZSNOrderItem> {
    return this.http.post<any>(this.toolUrl + '/orderitems', config).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private putOrderItem(orderItem: ZSNOrderItem): Observable<ZSNOrderItem> {
    const url = `${this.toolUrl}/orderitems/${orderItem.id}`;

    return this.http.put<any>(url, orderItem).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  getPageOrders(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: AROrderFilter,
  ): Observable<Page<ZSNOrder>> {
    let url = this.toolUrl + '/orders?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    url += fillOrderFilter(filter);
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data: Page<ZSNOrder> = response.data;
        for (const order of data.content) {
          this.handleOrderDates(order);
          if (order.campaign) {
            this.handleCampaignDates(order.campaign);
            if (order.campaign.configuration) {
              this.handleConfigurationDates(order.campaign.configuration);
            }
          }
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getOrder(id: number): Observable<ZSNOrder> {
    return this.http.get<any>(this.toolUrl + '/orders/' + id).pipe(
      map((response) => {
        const data: ZSNOrder = response.data;
        this.handleOrderDates(data);
        if (data.campaign) {
          this.handleCampaignDates(data.campaign);
          if (data.campaign.configuration) {
            this.handleConfigurationDates(data.campaign.configuration);
          }
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  saveOrder(order: ZSNOrder): Observable<ZSNOrder> {
    if (order.id) {
      return this.putOrder(order);
    }
    return this.postOrder(order);
  }

  deleteOrder(order: ZSNOrder) {
    const url = `${this.toolUrl}/orders/${order.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  getAreaForOrders(filter: AROrderFilter, specy: string): Observable<OrderSummary> {
    let url = `${this.toolUrl}/orders/area?`;
    url += fillOrderFilter(filter);
    if (specy != null) {
      url += `&specy=${specy}`;
    }
    return this.http.get<any>(url).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  exportKml(): Observable<Blob> {
    const url = this.toolUrl + '/kml';
    const applicationType = 'application/zip';
    return this.httpWanaka.blobQuery(url, applicationType);
  }

  // Add new orderItem
  private postOrder(order: ZSNOrder): Observable<ZSNOrder> {
    return this.http.post<any>(this.toolUrl + '/orders', order).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private putOrder(order: ZSNOrder): Observable<ZSNOrder> {
    const url = `${this.toolUrl}/orders/${order.id}`;

    return this.http.put<any>(url, order).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }
}
